import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Card,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import {
  CloudUpload,
  FileDownload,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Visibility,
  ThumbUp,
  Assessment,
  LocalGasStation,
  Bolt,
  AttachFile,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
// Scope1Data and Scope2Data types replaced by EmissionEntry from backend
import { usePermissions } from '../../hooks/usePermissions';
import { ApprovalDialog } from '../shared/ApprovalDialog';
import { ActivityDataEntryDialog } from './ActivityDataEntryDialog';
import { useApp } from '../../contexts/AuthContext';
import { emissionsService, EmissionEntry, attachmentService } from '../../services/emissionsService';
import { EmissionsTrendChart } from '../shared/EmissionsTrendChart';

export const Scope12DataCollection: React.FC = () => {
  const { user, selectedPeriod, facilities } = useApp();
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [entries, setEntries] = useState<EmissionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<EmissionEntry | null>(null);
  const [editingEntry, setEditingEntry] = useState<EmissionEntry | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [facilityFilter, setFacilityFilter] = useState<string>('all');

  // Evidence download state
  const [downloadingEvidence, setDownloadingEvidence] = useState(false);

  // Export menu state (Admin only)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  // Fetch emissions data from backend
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await emissionsService.listEntries({
        period: selectedPeriod?.year?.toString(),
      });
      setEntries(response.entries || []);
    } catch (err: any) {
      console.error('Failed to fetch emissions data:', err);
      setError('Failed to load emissions data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod?.year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter entries by scope
  const scope1Data = entries.filter(e => e.scope === 'scope_1');
  const scope2Data = entries.filter(e => e.scope === 'scope_2');

  // Category filter options
  const SCOPE1_CATEGORY_OPTIONS = [
    { value: 'all', label: 'All Categories' },
    { value: 'Stationary Combustion', label: 'Stationary Combustion' },
    { value: 'Mobile Combustion', label: 'Mobile Combustion' },
    { value: 'Fugitive Emissions', label: 'Fugitive Emissions' },
    { value: 'Process Emissions', label: 'Process Emissions' },
  ];

  const SCOPE2_CATEGORY_OPTIONS = [
    { value: 'all', label: 'All Categories' },
    { value: 'Purchased Electricity', label: 'Electricity' },
    { value: 'Purchased Heat', label: 'Heat' },
    { value: 'Purchased Steam', label: 'Steam' },
    { value: 'Purchased Cooling', label: 'Cooling' },
  ];

  // Facility filter options - derived from available facilities
  const facilityOptions = [
    { value: 'all', label: 'All Facilities' },
    ...facilities.map(f => ({ value: f.id, label: f.name }))
  ];

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: (files) => {
      console.log('Files uploaded:', files);
      setUploadDialogOpen(false);
    },
  });

  // Calculate summary stats - only count approved entries
  const approvedScope1 = scope1Data.filter(d => d.status === 'approved');
  const approvedScope2 = scope2Data.filter(d => d.status === 'approved');
  const scope1Total = approvedScope1.reduce((sum, item) => sum + (item.emissions_kg_co2e || 0) / 1000, 0); // Convert kg to tonnes
  const scope2Total = approvedScope2.reduce((sum, item) => sum + (item.emissions_kg_co2e || 0) / 1000, 0);
  const totalEmissions = scope1Total + scope2Total;
  const approvedCount = entries.filter(d => d.status === 'approved').length;
  const pendingCount = entries.filter(d => d.status === 'submitted' || d.status === 'pending').length;
  const totalCount = entries.length;

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { icon: React.ReactElement; color: string; bg: string; label: string }> = {
      approved: { icon: <CheckCircle sx={{ fontSize: 16 }} />, color: '#059669', bg: '#ecfdf5', label: 'Approved' },
      submitted: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      pending: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      draft: { icon: <Edit sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Draft' },
      rejected: { icon: <ErrorIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Rejected' },
      flagged: { icon: <ErrorIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Flagged' },
    };
    return configs[status] || configs.draft;
  };

  const handleDataSubmit = async (newData: any) => {
    try {
      const isScope1 = newData.scope === 'Scope 1';

      if (isScope1) {
        // Map frontend category values to backend enum values
        const categoryMap: Record<string, string> = {
          'stationary_combustion': 'Stationary Combustion',
          'mobile_combustion': 'Mobile Combustion',
          'fugitive_emissions': 'Fugitive Emissions',
          'process_emissions': 'Process Emissions',
        };

        const scope1Entry: any = {
          category: categoryMap[newData.category] || newData.category,
          subcategory: newData.fuelType || newData.subcategory,
          activity: newData.activity,  // Activity type (e.g., 'gaseous_fuels') for emission factor lookup
          fuel_type: newData.fuel_type || newData.fuelType,
          activity_amount: newData.activityAmount,
          activity_unit: newData.activityUnit,
          emission_factor_value: newData.emissionFactor,  // User-selected emission factor
          emission_factor_source: newData.emissionFactorSource,  // Source of the factor (Custom, DEFRA, etc.)
          facility_id: newData.facilityId,
          year: newData.year,
          month: newData.month,
          data_source: newData.dataSource || 'calculated',
          data_quality: newData.dataQuality || 'secondary',
          methodology: newData.methodology || 'GHG Protocol',
          notes: newData.notes,
          attachment_ids: newData.attachment_ids || [],
        };

        await emissionsService.createScope1Entry(scope1Entry);
      } else {
        // Map frontend category values to backend enum values for Scope 2
        const categoryMap: Record<string, string> = {
          'electricity': 'Purchased Electricity',
          'heat': 'Purchased Heat',
          'steam': 'Purchased Steam',
          'cooling': 'Purchased Cooling',
        };

        // Get the facility's electricity grid region
        const selectedFacility = facilities.find(f => f.id === newData.facilityId);

        // Derive grid region: prefer electricity_grid, fallback to country mapping
        let gridRegion = selectedFacility?.electricity_grid;

        // If electricity_grid not set or if it doesn't match the country, derive from country
        // Country can be either 2-letter code (IN, GB) or full name (India, United Kingdom)
        const countryToGrid: Record<string, string> = {
          // 2-letter codes (database format)
          'IN': 'IN',
          'GB': 'GB',
          'DE': 'DE',
          'US': 'US',
          'FR': 'FR',
          'CN': 'CN',
          'JP': 'JP',
          'AU': 'AU',
          'CA': 'CA',
          'BR': 'BR',
          // Full names
          'India': 'IN',
          'United Kingdom': 'GB',
          'UK': 'GB',
          'Germany': 'DE',
          'United States': 'US',
          'USA': 'US',
          'France': 'FR',
          'China': 'CN',
          'Japan': 'JP',
          'Australia': 'AU',
          'Canada': 'CA',
          'Brazil': 'BR',
        };

        const facilityCountry = selectedFacility?.country || '';
        const expectedGridCode = countryToGrid[facilityCountry] || '';

        // Override electricity_grid if it doesn't match the country
        if (!gridRegion || (expectedGridCode && !gridRegion.startsWith(expectedGridCode))) {
          gridRegion = expectedGridCode || gridRegion || 'GB';
        }

        const scope2Entry: any = {
          category: categoryMap[newData.category] || newData.category,
          method: newData.method || 'Location-Based',
          energy_source: newData.energySource || newData.category,
          consumption_amount: newData.activityAmount,
          consumption_unit: newData.activityUnit || 'kWh',
          emission_factor_value: newData.emissionFactor,  // User-selected emission factor
          emission_factor_source: newData.emissionFactorSource,  // Source of the factor (Custom, DEFRA, etc.)
          renewable_share: newData.renewableShare || 0,
          grid_region: gridRegion,
          facility_id: newData.facilityId,
          year: newData.year,
          month: newData.month,
          data_source: newData.dataSource || 'calculated',
          data_quality: newData.dataQuality || 'secondary',
          methodology: newData.methodology || 'GHG Protocol',
          notes: newData.notes,
          attachment_ids: newData.attachment_ids || [],
        };

        await emissionsService.createScope2Entry(scope2Entry);
      }

      // Refresh the data after successful submission
      await fetchData();
    } catch (err: any) {
      console.error('Failed to create emission entry:', err);
      console.error('Error data:', err?.data);
      console.error('Error status:', err?.status);
      console.error('Error message:', err?.message);

      // Check for validation errors from backend (Issue #6)
      const errorData = err?.data;
      if (errorData?.code === 'ERR_VALIDATION' && errorData?.validation_errors) {
        // Format validation errors for display
        const validationErrors = errorData.validation_errors;
        const errorMessage = Array.isArray(validationErrors)
          ? `Validation failed:\n• ${validationErrors.join('\n• ')}`
          : `Validation failed: ${errorData.message}`;
        setError(errorMessage);
      } else if (errorData?.detail) {
        // Handle other structured errors
        const detail = errorData.detail;
        if (typeof detail === 'object' && detail.validation_errors) {
          const errors = detail.validation_errors;
          setError(`Validation failed:\n• ${errors.join('\n• ')}`);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else if (detail.message) {
          setError(detail.message);
        } else {
          setError('Failed to save entry. Please check your input and try again.');
        }
      } else if (err?.message) {
        // Show the ApiError message
        setError(`Error: ${err.message}`);
      } else {
        setError('Failed to save entry. Please try again.');
      }
    }
  };

  const handleApprove = async (entryId: string) => {
    try {
      await emissionsService.approveEntry(entryId);
      await fetchData();
    } catch (err: any) {
      console.error('Failed to approve entry:', err);
      setError('Failed to approve entry. Please try again.');
    }
  };

  const handleReject = async (entryId: string) => {
    try {
      await emissionsService.rejectEntry(entryId, 'Rejected by reviewer');
      await fetchData();
    } catch (err: any) {
      console.error('Failed to reject entry:', err);
      setError('Failed to reject entry. Please try again.');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (window.confirm('Delete this entry?')) {
      try {
        await emissionsService.deleteEntry(entryId);
        await fetchData();
      } catch (err: any) {
        console.error('Failed to delete entry:', err);
        setError('Failed to delete entry. Please try again.');
      }
    }
  };

  const handleEdit = (entry: EmissionEntry) => {
    setEditingEntry(entry);
    setEntryDialogOpen(true);
  };

  const handleDataUpdate = async (updatedData: any) => {
    if (!editingEntry) return;

    try {
      const isScope1 = editingEntry.scope === 'scope_1';

      if (isScope1) {
        // Map frontend category values to backend enum values
        const categoryMap: Record<string, string> = {
          'stationary_combustion': 'Stationary Combustion',
          'mobile_combustion': 'Mobile Combustion',
          'fugitive_emissions': 'Fugitive Emissions',
          'process_emissions': 'Process Emissions',
        };

        const scope1Entry: any = {
          category: categoryMap[updatedData.category] || updatedData.category,
          subcategory: updatedData.activity || updatedData.fuelType || updatedData.subcategory,
          fuel_type: updatedData.fuel_type || updatedData.fuelType,
          activity_amount: updatedData.activityAmount,
          activity_unit: updatedData.activityUnit,
          facility_id: updatedData.facilityId,
          year: updatedData.year,
          month: updatedData.month,
          data_source: updatedData.dataSource || 'calculated',
          data_quality: updatedData.dataQuality || 'secondary',
          methodology: updatedData.methodology || 'GHG Protocol',
          notes: updatedData.notes,
        };

        await emissionsService.updateScope1Entry(editingEntry.id, scope1Entry);
      } else {
        // Map frontend category values to backend enum values for Scope 2
        const categoryMap: Record<string, string> = {
          'electricity': 'Purchased Electricity',
          'heat': 'Purchased Heat',
          'steam': 'Purchased Steam',
          'cooling': 'Purchased Cooling',
        };

        // Get the facility's electricity grid region
        const selectedFacility = facilities.find(f => f.id === updatedData.facilityId);

        // Derive grid region: prefer electricity_grid, fallback to country mapping
        let gridRegion = selectedFacility?.electricity_grid;

        const countryToGrid: Record<string, string> = {
          'IN': 'IN', 'GB': 'GB', 'DE': 'DE', 'US': 'US', 'FR': 'FR',
          'CN': 'CN', 'JP': 'JP', 'AU': 'AU', 'CA': 'CA', 'BR': 'BR',
          'India': 'IN', 'United Kingdom': 'GB', 'UK': 'GB', 'Germany': 'DE',
          'United States': 'US', 'USA': 'US', 'France': 'FR', 'China': 'CN',
          'Japan': 'JP', 'Australia': 'AU', 'Canada': 'CA', 'Brazil': 'BR',
        };

        const facilityCountry = selectedFacility?.country || '';
        const expectedGridCode = countryToGrid[facilityCountry] || '';

        if (!gridRegion || (expectedGridCode && !gridRegion.startsWith(expectedGridCode))) {
          gridRegion = expectedGridCode || gridRegion || 'GB';
        }

        const scope2Entry: any = {
          category: categoryMap[updatedData.category] || updatedData.category,
          method: updatedData.method || 'Location-Based',
          energy_source: updatedData.energySource || updatedData.category,
          consumption_amount: updatedData.activityAmount,
          consumption_unit: updatedData.activityUnit || 'kWh',
          renewable_share: updatedData.renewableShare || 0,
          grid_region: gridRegion,
          facility_id: updatedData.facilityId,
          year: updatedData.year,
          month: updatedData.month,
          data_source: updatedData.dataSource || 'calculated',
          data_quality: updatedData.dataQuality || 'secondary',
          methodology: updatedData.methodology || 'GHG Protocol',
          notes: updatedData.notes,
        };

        await emissionsService.updateScope2Entry(editingEntry.id, scope2Entry);
      }

      // Refresh the data after successful update
      setEditingEntry(null);
      await fetchData();
    } catch (err: any) {
      console.error('Failed to update emission entry:', err);
      const errorData = err?.data;
      if (errorData?.code === 'ERR_VALIDATION' && errorData?.validation_errors) {
        const validationErrors = errorData.validation_errors;
        const errorMessage = Array.isArray(validationErrors)
          ? `Validation failed:\n• ${validationErrors.join('\n• ')}`
          : `Validation failed: ${errorData.message}`;
        setError(errorMessage);
      } else if (errorData?.detail) {
        const detail = errorData.detail;
        if (typeof detail === 'object' && detail.validation_errors) {
          setError(`Validation failed:\n• ${detail.validation_errors.join('\n• ')}`);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else if (detail.message) {
          setError(detail.message);
        } else {
          setError('Failed to update entry. Please check your input and try again.');
        }
      } else if (err?.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError('Failed to update entry. Please try again.');
      }
    }
  };

  // Handle evidence export
  const handleExportEvidence = async () => {
    if (!permissions.canExport) return;

    setDownloadingEvidence(true);
    try {
      const scope = activeTab === 0 ? 1 : 2;
      const { url, filename } = await attachmentService.exportAuditEvidence(
        selectedPeriod?.year || new Date().getFullYear(),
        undefined, // all months
        scope
      );

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export evidence:', err);
      if (err?.status === 404) {
        setError('No evidence files found for the selected period.');
      } else {
        setError('Failed to export evidence. Please try again.');
      }
    } finally {
      setDownloadingEvidence(false);
    }
  };

  // Handle data export
  const handleExportData = async (format: 'csv' | 'xlsx') => {
    setExportMenuAnchor(null);
    if (!permissions.canExport) return;

    const dataToExport = activeTab === 0 ? filteredScope1Data : filteredScope2Data;
    if (dataToExport.length === 0) {
      setError('No data to export');
      return;
    }

    setExporting(true);
    try {
      const period = selectedPeriod ? `${selectedPeriod.year}` : undefined;
      const scope = activeTab === 0 ? 1 : 2;
      await emissionsService.exportData(format, period, scope);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getFilteredData = (data: EmissionEntry[]): EmissionEntry[] => {
    let filtered = data;

    // Permission-based filtering
    if (permissions.userRole === 'Admin') {
      // Admin sees all
    } else if (permissions.userRole === 'Viewer') {
      filtered = filtered.filter(item => item.status === 'approved');
    } else {
      // Manager, Contributor, Analyst: see own entries + approved entries
      filtered = filtered.filter(item => item.created_by === user?.id || item.created_by === user?.email || item.status === 'approved');
    }

    // Category filtering
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Facility filtering
    if (facilityFilter !== 'all') {
      filtered = filtered.filter(item => item.facility_id === facilityFilter);
    }

    return filtered;
  };

  const filteredScope1Data = getFilteredData(scope1Data);
  const filteredScope2Data = getFilteredData(scope2Data);

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const getCurrentPageData = (data: EmissionEntry[]) => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Reset page and filters when switching tabs
  useEffect(() => {
    setPage(0);
    setCategoryFilter('all');
    setFacilityFilter('all');
  }, [activeTab]);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
          Scope 1 & 2 Emissions
        </Typography>
      </Box>

      {/* Error Alert - supports multi-line validation errors */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, whiteSpace: 'pre-line' }}
          onClose={() => setError(null)}
        >
          {error.includes('\n') ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                {error.split('\n')[0]}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {error.split('\n').slice(1).map((line, idx) => (
                  <Typography key={idx} component="li" variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {line.replace(/^[•\-]\s*/, '')}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            error
          )}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#fef3c7', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <LocalGasStation sx={{ color: '#d97706' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Scope 1
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {scope1Total.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>tCO2e</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#dbeafe', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Bolt sx={{ color: '#2563eb' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Scope 2
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {scope2Total.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>tCO2e</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#f3e8ff', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Assessment sx={{ color: '#7c3aed' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Total
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {totalEmissions.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>tCO2e combined</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#d1fae5', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <CheckCircle sx={{ color: '#059669' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Data Status
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {approvedCount}/{totalCount}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
                  {pendingCount > 0 ? `${pendingCount} pending` : 'approved'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Emissions Trend Chart - Only show approved entries */}
      <EmissionsTrendChart
        data={entries
          .filter(e => e.status === 'approved')
          .map(e => ({
            month: e.month,
            year: e.year,
            source: e.scope === 'scope_1' ? 'Scope 1' : 'Scope 2',
            category: e.category, // Main categories: Fugitive Emissions, Mobile Combustion, etc.
            subcategory: e.fuel_type || e.activity_type || e.category, // Fuel type for Scope 1, activity type for Scope 2
            fuelType: e.fuel_type, // Diesel, LPG, Natural Gas, etc.
            value: (e.emissions_kg_co2e || 0) / 1000, // Convert to tCO2e
            facility_id: e.facility_id,
            facility_name: e.facility_name,
          }))}
        title="Emissions"
        color="#3b82f6"
        facilities={facilities.map(f => ({ id: f.id, name: f.name }))}
      />

      {/* Main Content Card */}
      <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
        {/* Tabs & Actions Header */}
        <Box sx={{
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#6b7280',
                minHeight: 48,
                px: 2,
              },
              '& .Mui-selected': {
                color: '#111827',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#2563eb',
                height: 2,
              },
            }}
          >
            <Tab label="Scope 1" />
            <Tab label="Scope 2" />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {permissions.canCreate && (
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 18 }} />}
                onClick={() => setEntryDialogOpen(true)}
                sx={{
                  backgroundColor: '#2563eb',
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' },
                }}
              >
                Add Entry
              </Button>
            )}
            {/* Export Data Button */}
            {permissions.canExport && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: 16 }} />}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  disabled={(activeTab === 0 ? filteredScope1Data : filteredScope2Data).length === 0 || exporting}
                  sx={{
                    borderColor: '#e5e7eb',
                    color: '#6b7280',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' },
                    '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' },
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={() => setExportMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleExportData('csv')}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => handleExportData('xlsx')}>Export as Excel</MenuItem>
                </Menu>
              </>
            )}
            {/* Export Evidence button */}
            {permissions.canExport && (
              <Button
                variant="outlined"
                size="small"
                startIcon={downloadingEvidence ? <CircularProgress size={14} /> : <AttachFile sx={{ fontSize: 16 }} />}
                onClick={handleExportEvidence}
                disabled={downloadingEvidence}
                sx={{
                  borderColor: '#9333ea',
                  color: '#9333ea',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { borderColor: '#7c3aed', backgroundColor: '#faf5ff' },
                  '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' },
                }}
              >
                {downloadingEvidence ? 'Exporting...' : 'Export Evidence'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Filter Bar */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
              sx={{ backgroundColor: '#fff', fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              {(activeTab === 0 ? SCOPE1_CATEGORY_OPTIONS : SCOPE2_CATEGORY_OPTIONS).map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Facility</InputLabel>
            <Select
              value={facilityFilter}
              label="Facility"
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setPage(0);
              }}
              sx={{ backgroundColor: '#fff', fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              {facilityOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {(categoryFilter !== 'all' || facilityFilter !== 'all') && (
            <Button
              size="small"
              onClick={() => {
                setCategoryFilter('all');
                setFacilityFilter('all');
                setPage(0);
              }}
              sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
            >
              Clear Filters
            </Button>
          )}

          <Typography sx={{ color: '#9ca3af', ml: 'auto', fontSize: '0.75rem' }}>
            {(activeTab === 0 ? filteredScope1Data : filteredScope2Data).length} entries
          </Typography>
        </Box>

        {/* Data Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>
                  {activeTab === 0 ? 'Fuel Type' : 'Energy Source'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>
                  {activeTab === 0 ? 'Activity' : 'Consumption'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Emissions (tCO2e)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Evidence</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem', width: 90 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getCurrentPageData(activeTab === 0 ? filteredScope1Data : filteredScope2Data).map((row) => {
                const status = getStatusConfig(row.status);
                const actions = permissions.getEntryActions(row.status, row.created_by);
                const emissionsTonnes = (row.emissions_kg_co2e || 0) / 1000;

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f9fafb' },
                      '& td': { py: 2, borderBottom: '1px solid #f3f4f6' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                        {row.category}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                        {row.facility_name || 'Unknown Facility'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        {row.fuel_type || row.activity_type || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        {row.activity_data?.toLocaleString() || '-'} {row.activity_unit || ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                        {emissionsTonnes.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                        {row.month && row.year ? (
                          <>
                            {new Date(row.year, row.month - 1).toLocaleString('default', { month: 'short' })} {row.year}
                          </>
                        ) : row.reporting_period || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                        {row.created_by ? row.created_by.split('@')[0].split('.').map((n: string) => n.charAt(0).toUpperCase() + n.slice(1)).join(' ') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={status.icon}
                        label={status.label}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 24,
                          backgroundColor: status.bg,
                          color: status.color,
                          fontWeight: 500,
                          '& .MuiChip-icon': { color: status.color },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(row as any).attachment_count > 0 ? (
                        <Tooltip title={`${(row as any).attachment_count} file${(row as any).attachment_count !== 1 ? 's' : ''} attached`}>
                          <Chip
                            icon={<AttachFile sx={{ fontSize: 14 }} />}
                            label={(row as any).attachment_count}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              backgroundColor: '#f3f4f6',
                              color: '#374151',
                              fontWeight: 500,
                              '& .MuiChip-icon': { color: '#6b7280' },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {actions.canApprove && (
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }}
                              sx={{ color: '#059669' }}
                            >
                              <ThumbUp sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {actions.canEdit && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              sx={{ color: '#6b7280' }}
                              onClick={() => handleEdit(row)}
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {actions.canDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.id)}
                              sx={{ color: '#dc2626' }}
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!actions.canEdit && !actions.canDelete && !actions.canApprove && (
                          <Tooltip title="View">
                            <IconButton size="small" sx={{ color: '#9ca3af' }}>
                              <Visibility sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={activeTab === 0 ? filteredScope1Data.length : filteredScope2Data.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            borderTop: '1px solid #e5e7eb',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
              color: '#6b7280',
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.8rem',
            },
          }}
        />
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Upload Emissions Data</DialogTitle>
        <DialogContent>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? '#3b82f6' : '#e5e7eb',
              borderRadius: 3,
              p: 5,
              textAlign: 'center',
              backgroundColor: isDragActive ? '#eff6ff' : '#f9fafb',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: '#3b82f6', backgroundColor: '#eff6ff' },
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151', mb: 1 }}>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              or click to browse (Excel, CSV)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setUploadDialogOpen(false)} sx={{ color: '#6b7280' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Activity Data Entry Dialog */}
      <ActivityDataEntryDialog
        open={entryDialogOpen}
        onClose={() => {
          setEntryDialogOpen(false);
          setEditingEntry(null);
        }}
        onSubmit={editingEntry ? handleDataUpdate : handleDataSubmit}
        scope={editingEntry ? (editingEntry.scope === 'scope_1' ? 'Scope 1' : 'Scope 2') : (activeTab === 0 ? 'Scope 1' : 'Scope 2')}
        editEntry={editingEntry}
      />

      {/* Approval Dialog */}
      {selectedEntry && (
        <ApprovalDialog
          open={approvalDialogOpen}
          onClose={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onApprove={() => { handleApprove(selectedEntry.id); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onReject={() => { handleReject(selectedEntry.id); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          entryData={{
            category: selectedEntry.category,
            value: (selectedEntry.emissions_kg_co2e || 0) / 1000,
            unit: 'tCO2e',
            uploadedBy: selectedEntry.created_by,
            uploadedAt: selectedEntry.created_at,
          }}
        />
      )}
    </Box>
  );
};
